import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useScrollAnimation } from '../../hooks/useScrollAnimation';
import { useReviews } from '../../hooks/useReviews';
import { GOOGLE_REVIEW_URL } from '../../lib/businessInfo';

// ── Types ──

interface Testimonial {
  quote: string;
  author: string;
  location: string;
  vehicle?: string;
  stars?: number;
  date?: string;
}

interface TrustBadge {
  icon: string;
  label: string;
  value?: string;
}

interface SocialProofProps {
  variant: 'stats' | 'testimonials' | 'carousel' | 'grid';
  theme?: 'light' | 'dark' | 'warm';
  title?: string;
  subtitle?: string;
  eyebrow?: string;
  testimonials?: Testimonial[];
  trustBadges?: TrustBadge[];
  showCta?: boolean;
  ctaText?: string;
  ctaHref?: string;
  secondaryCtaText?: string;
  secondaryCtaHref?: string;
  maxTestimonials?: number;
  useSupabaseReviews?: boolean;
  showAnimation?: boolean;
}

// ── Default Trust Badges ──

const DEFAULT_TRUST_BADGES: TrustBadge[] = [
  { icon: 'ri-shield-check-line', label: 'Honest, No-Pressure Sales' },
  { icon: 'ri-price-tag-3-line', label: 'Transparent Pricing Always' },
  { icon: 'ri-footprint-line', label: 'Every Sale Supports Oregon Dog Rescue' },
];

// ── Helper Functions ──

const formatDate = (dateStr: string) => {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - d.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  if (diffDays < 7) return `${diffDays} day${diffDays !== 1 ? 's' : ''} ago`;
  if (diffDays < 30) return `${Math.floor(diffDays / 7)} week${Math.floor(diffDays / 7) !== 1 ? 's' : ''} ago`;
  if (diffDays < 365) return `${Math.floor(diffDays / 30)} month${Math.floor(diffDays / 30) !== 1 ? 's' : ''} ago`;
  return d.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
};

const getInitials = (name: string) => {
  if (!name) return 'A';
  return name.split(' ').map((n) => n[0]).join('').toUpperCase().slice(0, 2);
};

// ── Theme Configurations ──

const THEME_CONFIGS = {
  light: {
    bg: 'bg-white',
    text: 'text-[#1A2332]',
    subtext: 'text-[#374151]',
    cardBg: 'bg-[#F7F5F0]',
    cardBorder: 'border-[#E8E2D6]',
    accent: 'text-[#92650a]',
    buttonPrimary: 'bg-[#1A2332] hover:bg-[#2C3E55] text-[#E8B84B]',
    buttonSecondary: 'bg-white hover:bg-[#F7F5F0] text-[#1A2332] border border-[#E8E2D6] hover:border-[#92650a]',
  },
  dark: {
    bg: 'bg-[#1C2B1E]',
    text: 'text-white',
    subtext: 'text-gray-300',
    cardBg: 'bg-white/5 backdrop-blur-sm',
    cardBorder: 'border-white/10 hover:border-[#E8B84B]/50',
    accent: 'text-[#E8B84B]',
    buttonPrimary: 'bg-[#E8B84B] hover:bg-[#D4A43A] text-[#1A2332]',
    buttonSecondary: 'bg-white/10 hover:bg-white/20 text-white border border-white/20',
  },
  warm: {
    bg: 'bg-[#F7F5F0]',
    text: 'text-[#1C2B1E]',
    subtext: 'text-gray-600',
    cardBg: 'bg-white',
    cardBorder: 'border-gray-200',
    accent: 'text-[#92650a]',
    buttonPrimary: 'bg-[#1C2B1E] hover:bg-[#2C3E55] text-[#E8B84B]',
    buttonSecondary: 'bg-white hover:bg-gray-50 text-[#1C2B1E] border border-gray-200',
  },
};

// ── Main Component ──

export default function SocialProof({
  variant,
  theme = 'light',
  title,
  subtitle,
  eyebrow,
  testimonials = [],
  trustBadges = DEFAULT_TRUST_BADGES,
  showCta = true,
  ctaText = 'Read All Reviews',
  ctaHref = '/reviews',
  secondaryCtaText = 'Write a Review',
  secondaryCtaHref = '/write-a-review',
  maxTestimonials = 5,
  useSupabaseReviews = false,
  showAnimation = true,
}: SocialProofProps) {
  const { ref, isVisible } = useScrollAnimation();
  const { reviews, loading } = useReviews();
  const [activeIndex, setActiveIndex] = useState(0);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const carouselRef = useRef<HTMLDivElement>(null);

  // Respect prefers-reduced-motion — read once on mount
  const prefersReducedMotion = useRef(
    typeof window !== 'undefined' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches
  );

  const themeConfig = THEME_CONFIGS[theme];

  // Determine testimonials source
  let displayTestimonials = testimonials;
  if (useSupabaseReviews && reviews.length > 0) {
    displayTestimonials = reviews
      .filter((r) => r.rating >= 4)
      .slice(0, maxTestimonials)
      .map((r) => ({
        quote: r.text,
        author: r.name,
        location: r.location,
        vehicle: r.vehicle_purchased,
        stars: r.rating,
        date: r.created_at,
      }));
  }

  const safeIndex = activeIndex >= displayTestimonials.length ? 0 : activeIndex;
  const currentTestimonial = displayTestimonials[safeIndex];

  const animationClass = !showAnimation
    ? 'opacity-100 translate-y-0'
    : isVisible
      ? 'opacity-100 translate-y-0'
      : 'opacity-0 translate-y-6';

  // ── Auto-advance helpers ──
  const startAutoPlay = (len: number) => {
    if (prefersReducedMotion.current) return;
    if (len <= 1) return;
    if (intervalRef.current) clearInterval(intervalRef.current);
    intervalRef.current = setInterval(() => {
      setActiveIndex((prev) => (prev + 1) % len);
    }, 5000);
  };

  // Auto-advance carousel (paused for reduced-motion users)
  useEffect(() => {
    if (variant !== 'carousel') return;
    startAutoPlay(displayTestimonials.length);
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [variant, displayTestimonials.length]);

  // Navigate to a specific index and restart auto-advance
  const goTo = (idx: number) => {
    setActiveIndex(idx);
    startAutoPlay(displayTestimonials.length);
  };

  const goPrev = () => {
    goTo((safeIndex - 1 + displayTestimonials.length) % displayTestimonials.length);
  };

  const goNext = () => {
    goTo((safeIndex + 1) % displayTestimonials.length);
  };

  // ── Keyboard arrow-key support ──
  useEffect(() => {
    if (variant !== 'carousel') return;
    const el = carouselRef.current;
    if (!el) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        goPrev();
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        goNext();
      }
    };

    el.addEventListener('keydown', handleKeyDown);
    return () => el.removeEventListener('keydown', handleKeyDown);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [variant, safeIndex, displayTestimonials.length]);

  // ── Variant: Stats (Trust Badges Only) ──

  if (variant === 'stats') {
    return (
      <section className={`py-12 ${themeConfig.bg}`} ref={ref}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {(title || subtitle || eyebrow) && (
            <div className={`text-center mb-8 transition-all duration-700 ${animationClass}`}>
              {eyebrow && (
                <div className={`inline-flex items-center gap-2 ${themeConfig.cardBg} border ${themeConfig.cardBorder} rounded-full px-4 py-2 mb-4`}>
                  <span className="w-4 h-4 flex items-center justify-center" aria-hidden="true">
                    <i className={`ri-star-line ${themeConfig.accent}`}></i>
                  </span>
                  <span className={`text-sm font-medium ${themeConfig.subtext} uppercase tracking-wider`}>{eyebrow}</span>
                </div>
              )}
              {title && (
                <h2 className={`text-3xl sm:text-4xl font-bold ${themeConfig.text} mb-3`} style={{ fontFamily: 'Oswald, sans-serif' }}>
                  {title}
                </h2>
              )}
              {subtitle && <p className={`text-lg ${themeConfig.subtext}`}>{subtitle}</p>}
            </div>
          )}

          <div className={`flex flex-wrap items-center justify-center gap-6 sm:gap-8 transition-all duration-700 delay-200 ${animationClass}`}>
            {trustBadges.map((badge, index) => (
              <div key={index} className="flex items-center gap-2">
                <span className="w-5 h-5 flex items-center justify-center" aria-hidden="true">
                  <i className={`${badge.icon} ${themeConfig.accent} text-xl`}></i>
                </span>
                <span className={`text-sm ${themeConfig.text}`}>{badge.label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>
    );
  }

  // ── Variant: Carousel (Single Rotating Testimonial) ──

  if (variant === 'carousel') {
    return (
      <section className={`py-14 ${themeConfig.bg}`} ref={ref}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          {(title || subtitle || eyebrow) && (
            <div className={`text-center mb-8 transition-all duration-700 ${animationClass}`}>
              {eyebrow && (
                <div className={`inline-flex items-center gap-2 ${themeConfig.cardBg} border ${themeConfig.cardBorder} rounded-full px-4 py-2 mb-5 sm:mb-6`}>
                  <span className="w-4 h-4 flex items-center justify-center" aria-hidden="true">
                    <i className={`ri-chat-1-line ${themeConfig.accent}`}></i>
                  </span>
                  <span className={`text-sm font-medium ${themeConfig.subtext} uppercase tracking-wider`}>{eyebrow}</span>
                </div>
              )}
              {title && (
                <h2 className={`text-3xl sm:text-4xl lg:text-5xl font-bold ${themeConfig.text} mb-4 sm:mb-6`} style={{ fontFamily: 'Oswald, sans-serif' }}>
                  {title}
                </h2>
              )}
              {subtitle && <p className={`text-base sm:text-lg ${themeConfig.subtext} max-w-3xl mx-auto`}>{subtitle}</p>}
            </div>
          )}

          {/* Carousel */}
          <div
            className={`relative transition-all duration-1000 delay-200 ${animationClass}`}
            ref={carouselRef}
            tabIndex={displayTestimonials.length > 1 ? 0 : undefined}
            aria-label="Testimonials carousel — use left and right arrow keys to navigate"
            role="region"
            style={{ outline: 'none' }}
          >
            {loading ? (
              <div className={`${themeConfig.cardBg} rounded-3xl p-6 sm:p-8 md:p-12 border ${themeConfig.cardBorder} shadow-xl animate-pulse`}>
                <div className="max-w-4xl mx-auto text-center space-y-4">
                  <div className="flex justify-center gap-1 mb-4">
                    {[...Array(5)].map((_, i) => (
                      <div key={i} className="w-6 h-6 bg-gray-200 rounded-full" />
                    ))}
                  </div>
                  <div className="h-6 bg-gray-200 rounded w-3/4 mx-auto" />
                  <div className="h-6 bg-gray-200 rounded w-2/3 mx-auto" />
                </div>
              </div>
            ) : displayTestimonials.length === 0 ? (
              <div className={`${themeConfig.cardBg} rounded-3xl p-6 sm:p-8 md:p-12 border ${themeConfig.cardBorder} shadow-xl text-center`}>
                <span className="w-12 h-12 flex items-center justify-center mx-auto mb-4" aria-hidden="true">
                  <i className={`ri-star-line text-5xl ${themeConfig.accent}`}></i>
                </span>
                <p className={`${themeConfig.subtext} text-lg mb-4`}>No reviews yet — be the first!</p>
                <Link
                  to={secondaryCtaHref}
                  className={`inline-flex items-center gap-2 ${themeConfig.buttonPrimary} px-6 py-3 rounded-lg font-semibold transition-all duration-300 whitespace-nowrap`}
                  style={{ fontFamily: 'Oswald, sans-serif' }}
                >
                  <span className="w-4 h-4 flex items-center justify-center" aria-hidden="true">
                    <i className="ri-edit-line"></i>
                  </span>
                  {secondaryCtaText}
                </Link>
              </div>
            ) : (
              <>
                {/* Arrow buttons + card row */}
                <div className="flex items-center gap-3 sm:gap-4">
                  {/* Prev arrow */}
                  {displayTestimonials.length > 1 && (
                    <button
                      onClick={goPrev}
                      aria-label={`Previous review (${safeIndex === 0 ? displayTestimonials.length : safeIndex} of ${displayTestimonials.length})`}
                      className={`flex-shrink-0 w-10 h-10 sm:w-12 sm:h-12 flex items-center justify-center rounded-full border-2 ${themeConfig.cardBorder} ${themeConfig.cardBg} hover:border-[#E8B84B] hover:text-[#92650a] ${themeConfig.text} transition-all duration-200 cursor-pointer shadow-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-[#E8B84B]`}
                    >
                      <span className="w-5 h-5 flex items-center justify-center" aria-hidden="true">
                        <i className="ri-arrow-left-s-line text-xl"></i>
                      </span>
                    </button>
                  )}

                  {/* Card */}
                  <div className={`flex-1 ${themeConfig.cardBg} rounded-3xl p-6 sm:p-8 md:p-12 border ${themeConfig.cardBorder} shadow-xl`}>
                    <div className="max-w-4xl mx-auto" aria-live="polite" aria-atomic="true">
                      {/* Stars */}
                      <div className="flex justify-center gap-1 mb-4 sm:mb-6">
                        {[...Array(currentTestimonial?.stars ?? 5)].map((_, i) => (
                          <span key={i} className={`text-xl sm:text-2xl ${themeConfig.accent}`} aria-hidden="true">★</span>
                        ))}
                      </div>

                      {/* Quote */}
                      <figure className={`text-base sm:text-xl md:text-2xl ${themeConfig.text} text-center mb-6 sm:mb-8 leading-relaxed font-medium`}>
                        <blockquote>
                          <p>&ldquo;{currentTestimonial?.quote}&rdquo;</p>
                        </blockquote>
                        <figcaption className="mt-6 sm:mt-8 mb-2">
                          <div className="flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-4">
                            <div
                              className={`w-9 h-9 ${theme === 'dark' ? 'bg-[#E8B84B] text-[#1A2332]' : 'bg-[#1A2332] text-[#E8B84B]'} rounded-full flex items-center justify-center font-bold text-xs flex-shrink-0`}
                              style={{ fontFamily: 'Oswald, sans-serif' }}
                              aria-hidden="true"
                            >
                              {getInitials(currentTestimonial?.author ?? '')}
                            </div>
                            <div className="text-center sm:text-left">
                              <span className={`font-bold ${themeConfig.text} text-base sm:text-lg block`} style={{ fontFamily: 'Oswald, sans-serif' }}>
                                {currentTestimonial?.author}
                              </span>
                              <div className={`${themeConfig.subtext} text-sm`}>
                                {currentTestimonial?.location}
                                {currentTestimonial?.vehicle ? ` • ${currentTestimonial.vehicle}` : ''}
                              </div>
                              {currentTestimonial?.date && (
                                <div className={`${themeConfig.subtext} text-xs opacity-75`}>
                                  {formatDate(currentTestimonial.date)}
                                </div>
                              )}
                            </div>
                          </div>
                        </figcaption>
                      </figure>
                    </div>
                  </div>

                  {/* Next arrow */}
                  {displayTestimonials.length > 1 && (
                    <button
                      onClick={goNext}
                      aria-label={`Next review (${(safeIndex + 2) > displayTestimonials.length ? 1 : safeIndex + 2} of ${displayTestimonials.length})`}
                      className={`flex-shrink-0 w-10 h-10 sm:w-12 sm:h-12 flex items-center justify-center rounded-full border-2 ${themeConfig.cardBorder} ${themeConfig.cardBg} hover:border-[#E8B84B] hover:text-[#92650a] ${themeConfig.text} transition-all duration-200 cursor-pointer shadow-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-[#E8B84B]`}
                    >
                      <span className="w-5 h-5 flex items-center justify-center" aria-hidden="true">
                        <i className="ri-arrow-right-s-line text-xl"></i>
                      </span>
                    </button>
                  )}
                </div>

                {/* Navigation Dots + Slide Counter */}
                {displayTestimonials.length > 1 && (
                  <div className="flex justify-center items-center gap-3 mt-6 sm:mt-8">
                    {/* Dots */}
                    <ol
                      className="flex items-center gap-1"
                      aria-label="Testimonial navigation"
                    >
                      {displayTestimonials.map((t, index) => (
                        <li key={index}>
                          <button
                            onClick={() => goTo(index)}
                            aria-label={`Go to testimonial ${index + 1} of ${displayTestimonials.length}: review by ${t.author}`}
                            aria-current={index === safeIndex ? 'true' : undefined}
                            className="flex items-center justify-center cursor-pointer flex-shrink-0 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#E8B84B] focus-visible:rounded"
                            style={{ minWidth: '44px', minHeight: '44px', background: 'none', border: 'none', padding: '0' }}
                          >
                            <span
                              className="rounded-full block pointer-events-none"
                              style={{
                                width: '32px',
                                height: '12px',
                                transformOrigin: 'center',
                                backgroundColor: index === safeIndex
                                  ? '#E8B84B'
                                  : theme === 'dark' ? 'rgba(255,255,255,0.2)' : '#D8D0BC',
                                transform: index === safeIndex ? 'scaleX(1)' : 'scaleX(0.375)',
                                transition: prefersReducedMotion.current
                                  ? 'none'
                                  : 'transform 0.3s ease-out, background-color 0.3s ease-out',
                              }}
                            />
                          </button>
                        </li>
                      ))}
                    </ol>

                    {/* Slide counter */}
                    <span
                      className={`text-xs font-semibold tabular-nums select-none ${themeConfig.subtext} opacity-70`}
                      aria-live="polite"
                      aria-atomic="true"
                      style={{ fontFamily: 'Oswald, sans-serif', minWidth: '2.5rem', textAlign: 'center' }}
                    >
                      {safeIndex + 1} / {displayTestimonials.length}
                    </span>
                  </div>
                )}
              </>
            )}
          </div>

          {/* CTA */}
          {showCta && (
            <div className={`text-center mt-10 transition-all duration-700 delay-200 ${animationClass}`}>
              <p className={`${themeConfig.subtext} mb-5 sm:mb-6`}>
                Transparent pricing, no pressure, and a portion of every sale goes to Oregon Dog Rescue.
              </p>
              <div className="flex flex-col sm:flex-row flex-wrap justify-center gap-3 sm:gap-4">
                <Link
                  to={ctaHref}
                  className={`inline-flex items-center justify-center gap-2 ${themeConfig.buttonPrimary} px-6 sm:px-8 py-3 sm:py-4 rounded-lg font-semibold transition-all duration-300 shadow-lg hover:shadow-xl whitespace-nowrap`}
                  style={{ fontFamily: 'Oswald, sans-serif' }}
                >
                  <span className="w-4 h-4 flex items-center justify-center" aria-hidden="true">
                    <i className="ri-star-line"></i>
                  </span>
                  {ctaText}
                </Link>
                <Link
                  to={secondaryCtaHref}
                  className={`inline-flex items-center justify-center gap-2 ${themeConfig.buttonSecondary} px-6 sm:px-8 py-3 sm:py-4 rounded-lg font-semibold transition-all duration-300 whitespace-nowrap`}
                  style={{ fontFamily: 'Oswald, sans-serif' }}
                >
                  <span className="w-4 h-4 flex items-center justify-center" aria-hidden="true">
                    <i className="ri-edit-line"></i>
                  </span>
                  {secondaryCtaText}
                </Link>
              </div>
            </div>
          )}
        </div>
      </section>
    );
  }

  // ── Variant: Grid (Multiple Testimonial Cards) ──

  if (variant === 'grid') {
    return (
      <section className={`py-16 lg:py-20 ${themeConfig.bg}`} ref={ref}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          {(title || subtitle || eyebrow) && (
            <div className={`text-center mb-12 transition-all duration-700 ${animationClass}`}>
              {eyebrow && (
                <div className={`inline-flex items-center gap-2 ${themeConfig.cardBg} border ${themeConfig.cardBorder} rounded-full px-4 py-2 mb-4`}>
                  <span className="w-4 h-4 flex items-center justify-center" aria-hidden="true">
                    <i className={`ri-star-line ${themeConfig.accent}`}></i>
                  </span>
                  <span className={`text-xs font-bold ${themeConfig.subtext} uppercase tracking-widest`}>{eyebrow}</span>
                </div>
              )}
              {title && (
                <h2 className={`text-3xl lg:text-4xl font-bold ${themeConfig.text} mb-3`} style={{ fontFamily: 'Oswald, sans-serif' }}>
                  {title}
                </h2>
              )}
              {subtitle && <p className={`${themeConfig.subtext} text-lg`}>{subtitle}</p>}
            </div>
          )}

          {/* Grid */}
          {loading ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[...Array(3)].map((_, i) => (
                <div key={i} className={`${themeConfig.cardBg} rounded-xl p-6 border ${themeConfig.cardBorder} animate-pulse`}>
                  <div className="flex gap-1 mb-4">
                    {[...Array(5)].map((__, j) => (
                      <div key={j} className="w-5 h-5 bg-gray-200 rounded-full" />
                    ))}
                  </div>
                  <div className="h-4 bg-gray-200 rounded w-full mb-2" />
                  <div className="h-4 bg-gray-200 rounded w-5/6 mb-5" />
                </div>
              ))}
            </div>
          ) : displayTestimonials.length === 0 ? (
            <div className={`${themeConfig.cardBg} rounded-2xl p-12 border ${themeConfig.cardBorder} text-center`}>
              <span className="w-12 h-12 flex items-center justify-center mx-auto mb-4" aria-hidden="true">
                <i className={`ri-star-line text-5xl ${themeConfig.accent}`}></i>
              </span>
              <h3 className={`text-xl font-bold ${themeConfig.text} mb-2`}>No Reviews Yet</h3>
              <p className={`${themeConfig.subtext} mb-6`}>Be the first to share your experience with us.</p>
              <Link
                to={secondaryCtaHref}
                className={`inline-flex items-center gap-2 ${themeConfig.buttonPrimary} px-6 py-3 rounded-lg font-medium transition-colors whitespace-nowrap`}
              >
                <span className="w-4 h-4 flex items-center justify-center" aria-hidden="true">
                  <i className="ri-edit-line"></i>
                </span>
                {secondaryCtaText}
              </Link>
            </div>
          ) : (
            <div className={`grid md:grid-cols-2 lg:grid-cols-3 gap-6 transition-all duration-700 delay-200 ${animationClass}`}>
              {displayTestimonials.map((testimonial, index) => (
                <div
                  key={index}
                  className={`${themeConfig.cardBg} rounded-xl p-6 border ${themeConfig.cardBorder} transition-all duration-200`}
                >
                  {/* Quote Icon */}
                  <div className="mb-4">
                    <span className={`text-4xl ${themeConfig.accent} opacity-50`} aria-hidden="true">&ldquo;</span>
                  </div>

                  {/* Stars */}
                  {testimonial.stars && (
                    <div className="flex gap-1 mb-4">
                      {[...Array(testimonial.stars)].map((_, i) => (
                        <span key={i} className={`text-sm ${themeConfig.accent}`} aria-hidden="true">★</span>
                      ))}
                    </div>
                  )}

                  {/* Quote */}
                  <p className={`${themeConfig.text} leading-relaxed mb-6 italic text-sm`}>
                    &ldquo;{testimonial.quote}&rdquo;
                  </p>

                  {/* Author */}
                  <div className={`border-t ${theme === 'dark' ? 'border-white/10' : 'border-gray-200'} pt-4`}>
                    <p className={`font-bold ${themeConfig.text} mb-1 text-sm`}>{testimonial.author}</p>
                    <p className={`text-xs ${themeConfig.subtext}`}>{testimonial.location}</p>
                    {testimonial.vehicle && (
                      <p className={`text-xs ${themeConfig.accent} mt-2`}>{testimonial.vehicle}</p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Trust Badges */}
          {trustBadges.length > 0 && (
            <div className={`mt-12 flex flex-wrap items-center justify-center gap-8 text-sm ${themeConfig.subtext} transition-all duration-700 delay-300 ${animationClass}`}>
              {trustBadges.map((badge, index) => (
                <div key={index} className="flex items-center gap-2">
                  <span className="w-5 h-5 flex items-center justify-center" aria-hidden="true">
                    <i className={`${badge.icon} ${themeConfig.accent} text-xl`}></i>
                  </span>
                  <span>{badge.label}</span>
                </div>
              ))}
            </div>
          )}

          {/* CTA */}
          {showCta && (
            <div className={`text-center mt-8 transition-all duration-700 delay-300 ${animationClass}`}>
              <Link
                to={ctaHref}
                className={`inline-flex items-center gap-2 ${themeConfig.subtext} hover:${themeConfig.accent} font-semibold text-sm transition-colors cursor-pointer`}
              >
                {ctaText}
                <span aria-hidden="true">→</span>
              </Link>
            </div>
          )}
        </div>
      </section>
    );
  }

  // ── Variant: Testimonials (Grid + Trust Badges) ──

  return (
    <section className={`py-16 lg:py-20 ${themeConfig.bg}`} ref={ref}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        {(title || subtitle || eyebrow) && (
          <div className={`text-center mb-12 transition-all duration-700 ${animationClass}`}>
            {title && (
              <h2 className={`text-3xl lg:text-4xl font-bold ${themeConfig.text} mb-3`} style={{ fontFamily: 'Oswald, sans-serif' }}>
                {title}
              </h2>
            )}
            {subtitle && <p className={`${themeConfig.subtext} text-lg`}>{subtitle}</p>}
          </div>
        )}

        {/* Testimonials Grid */}
        <div className={`grid md:grid-cols-3 gap-6 mb-12 transition-all duration-700 delay-200 ${animationClass}`}>
          {displayTestimonials.map((testimonial, index) => (
            <div
              key={index}
              className={`${themeConfig.cardBg} rounded-xl p-6 border ${themeConfig.cardBorder} transition-colors duration-200`}
            >
              <div className="mb-4">
                <span className={`text-4xl ${themeConfig.accent} opacity-50`} aria-hidden="true">&ldquo;</span>
              </div>
              <p className={`${themeConfig.text} leading-relaxed mb-6 italic`}>
                &ldquo;{testimonial.quote}&rdquo;
              </p>
              <div className={`border-t ${theme === 'dark' ? 'border-white/10' : 'border-gray-200'} pt-4`}>
                <p className={`font-bold ${themeConfig.text} mb-1`}>{testimonial.author}</p>
                <p className={`text-sm ${themeConfig.subtext}`}>{testimonial.location}</p>
                {testimonial.vehicle && (
                  <p className={`text-sm ${themeConfig.accent} mt-2`}>{testimonial.vehicle}</p>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* Trust Badges */}
        <div className={`flex flex-wrap items-center justify-center gap-8 text-sm ${themeConfig.subtext} transition-all duration-700 delay-300 ${animationClass}`}>
          {trustBadges.map((badge, index) => (
            <div key={index} className="flex items-center gap-2">
              <span className="w-5 h-5 flex items-center justify-center" aria-hidden="true">
                <i className={`${badge.icon} ${themeConfig.accent} text-xl`}></i>
              </span>
              <span>{badge.label}</span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}